/**
 * Theme management composable for WebIMAP
 *
 * Thin wrapper around VueUse useColorMode providing theme switching
 * with automatic localStorage persistence and system preference detection.
 */

import { useColorMode } from '@vueuse/core';
import type { ThemeName } from '@/types/theme';
import { defaultThemeConfig } from '@/types/theme';

/**
 * Theme composable hook
 *
 * Provides reactive theme state management with:
 * - Automatic localStorage persistence
 * - System preference detection (prefers-color-scheme)
 * - data-theme attribute synchronization
 *
 * @returns Theme state and controls
 *
 * @example
 * ```vue
 * <script setup>
 * import { useTheme } from '@/composables/useTheme'
 *
 * const { mode, setTheme } = useTheme()
 *
 * // Switch to dark theme
 * setTheme('dark')
 *
 * // Check current theme
 * console.log(mode.value) // 'dark'
 * </script>
 * ```
 */
export function useTheme() {
  // Initialize VueUse color mode with our configuration
  const mode = useColorMode({
    attribute: defaultThemeConfig.attribute,
    modes: defaultThemeConfig.modes,
    storageKey: defaultThemeConfig.storageKey,
    // Initial value: auto-detect from system preference
    initialValue: 'auto'
  });

  /**
   * Set theme programmatically
   */
  function setTheme(theme: ThemeName) {
    mode.value = theme;
  }

  /**
   * Get current theme name
   */
  function getCurrentTheme(): ThemeName {
    return mode.value as ThemeName;
  }

  return {
    /** Reactive theme mode (read/write) */
    mode,

    /** Set theme programmatically */
    setTheme,

    /** Get current theme name */
    getCurrentTheme
  };
}
